import type { SourceItems } from '@petsmart-ui/sparky';

import { BREAKPOINTS, sanitizeUrl } from '@libs/common-next';

import type { PictureSize } from './type';

// creates optimised scene7 src value (url)
export const createScene7ImgUrl = (src?: string, size?: PictureSize, quality?: number, format = 'webp') => {
    const fmt = format ? `?fmt=${format}` : '';
    const width = size?.width ? `&wid=${size.width}` : '';
    const height = size?.height ? `&hei=${size.height}` : '';
    const qlt = quality && quality !== 100 ? `&qlt=${quality}` : '';
    return `${src}${fmt}${width}${height}${qlt}`;
};

// creates optimised scene7 srcSet value (url)
export const createScene7SrcSetValue = (baseImgUrl: string, size: PictureSize, format = 'webp'): string =>
    `${baseImgUrl}?fmt=${format}&wid=${size.width}&hei=${
        size.height
    } 1x, ${baseImgUrl}?fmt=${format}&wid=${size.width * 2}&hei=${size.height * 2} 2x`;

// uses image url and defined picture sizes to create an array of srcSet values
export const createScene7SourceSets = (baseImgUrl: string, sizes: PictureSize[], screenSizes: number[]): string[] => {
    // default webp format
    const imgSrcSetWebp = screenSizes
        .filter((_, index) => sizes[index])
        .map((_, index) => createScene7SrcSetValue(baseImgUrl, sizes[index]));
    // fallback jpg format
    const imgSrcSetFallback = screenSizes
        .filter((_, index) => sizes[index])
        .map((_, index) => createScene7SrcSetValue(baseImgUrl, sizes[index], 'jpg'));
    return [...imgSrcSetWebp, ...imgSrcSetFallback];
};

// Mapps srcSet strings and generates items prop for sparky/Picture
export const generatePictureItems = (srcSetArray: string[], screenSizes: number[]): SourceItems =>
    srcSetArray.map((srcSet, index) => {
        const sizeIndex = index >= 3 ? index - 3 : index;
        return {
            srcSet,
            media: `(min-width: ${screenSizes[sizeIndex]}px)`,
        };
    });

// Generates optimized imgSrc and items array used in Sparky/Picture
// Note: this logic, or a version of it, could be moved to Sparky.
export const generateOptimizedPictureProps = (
    src: string,
    sizes?: PictureSize[],
    quality?: number
): { imgSrc: string; pictureItems?: SourceItems; quality?: number } => {
    sizes = sizes || [];
    const defaultScreenSizes = [BREAKPOINTS.lg, BREAKPOINTS.md, BREAKPOINTS.sm2]; // desktop, tablet, mobile
    let pictureItems: SourceItems = [];
    src = sanitizeUrl(src);
    let optimizedImgUrl: string = src;

    // use appropriate optimization func based on image origin
    const isScene7 = src.includes('scene7') || src.includes('/is/image/');
    if (isScene7) {
        // remove existing optimization
        let baseUrl = src;
        if (src.includes('?')) {
            [baseUrl] = src.split('?');
        }
        pictureItems = generatePictureItems(
            createScene7SourceSets(baseUrl, sizes, defaultScreenSizes),
            defaultScreenSizes
        );
        optimizedImgUrl = createScene7ImgUrl(baseUrl, sizes[sizes.length - 1], quality);
    }

    return {
        imgSrc: optimizedImgUrl,
        pictureItems,
    };
};
